import { Nullable } from '@crossbeam/types';

import {
  Partner,
  isManagedOfflinePartner,
  isOfflinePartner,
} from '@/types/partners';

function normalizeDomain(domain: Nullable<string> | undefined) {
  return (
    domain
      ?.trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '') ?? ''
  );
}

/**
 * Finds an existing partner matching any of `domains`, for deciding whether a
 * company picked in the invite search can still be invited.
 *
 * The invite search merges discoverable Crossbeam orgs with Clearbit results,
 * and the Crossbeam side already drops orgs we partner with. That leaves the
 * Clearbit twin selectable with no public_invite_code, so the domain is the
 * only signal left to match on.
 *
 * Offline partners are ignored because inviting them onto Crossbeam is a
 * supported flow.
 *
 * @param partners - Current partner orgs, e.g. partnersStore.partnerOrgs
 * @param domains - Domains to match, e.g. a company's domain and clearbit_domain
 * @returns The matching partner, or null when the company is invitable
 */
export function findPartnerByDomains(
  partners: Partner[],
  domains: (Nullable<string> | undefined)[],
): Partner | null {
  const normalized = new Set(domains.map(normalizeDomain).filter(Boolean));
  if (!normalized.size) return null;
  const match = partners.find(
    (partner) =>
      !isOfflinePartner(partner) &&
      !isManagedOfflinePartner(partner) &&
      [partner.domain, partner.clearbit_domain].some((domain) =>
        normalized.has(normalizeDomain(domain)),
      ),
  );
  return match ?? null;
}
